// 專案成員（「成員」面板）要用的純計算：角色順序 / 名稱、依角色分組、「專案的製作能做什麼」那一句說明。
//
// 這個檔案前端（成員面板）和後端測試（cf-worker/members.test.mjs 拿裡面的說明去對路由和權限）共用，
// 所以只能放「純判斷」：不 import 別的檔案、不碰 DOM / React、不用 enum / namespace 這類要編譯的寫法。
//
// 角色一律是「這個專案裡的角色」：製作（admin）= 這個專案的製作，只在這個專案有效，不是全站製作。
//
// 視覺統籌（visual_lead）：資料庫存成 animator + visual_lead=1，畫面和 API 上是獨立的角色（規則：reviewGate.ts）。
// 專案的製作（帳號不是全站製作）可以自己加、移出動畫師、視覺統籌和製作，也可以互相改；
// 導演一律要請全站製作設定（後台擋：cf-worker/members.mjs，測試對過真的路由）。

export type MemberRole = 'admin' | 'director' | 'visual_lead' | 'animator';

/** 面板上分組的順序：製作、導演、視覺統籌、動畫師 */
export const MEMBER_ROLE_ORDER: readonly MemberRole[] = ['admin', 'director', 'visual_lead', 'animator'];

export const MEMBER_ROLE_NAME: Record<MemberRole, string> = {
  admin: 'PM',
  director: '導演',
  visual_lead: '視覺統籌',
  animator: '動畫師',
};

/**
 * 導演在「調整舞台」能做的事（每一項都有測試去對後端的權限：cf-worker/directorStage.test.mjs）。
 * 物件的顯示 / 位置、刪 cue、改編號、設定機關這些導演還是不能做（後端會擋）。
 */
export const DIRECTOR_STAGE_TEXT = '調整舞台（調機關位置、存回 Cue 庫、另存成新 cue、複製、改名、轉場秒數）';

/**
 * 專案的製作想動導演時，畫面上寫的那一句（也是後台回 403 的話：cf-worker/members.mjs PM_DIRECTOR_DENIED，測試會對）。
 * 專案的製作可以加 / 移出動畫師和製作，導演要請全站製作。
 */
export const PROJECT_ADMIN_DIRECTOR_TEXT = '導演要請全站製作設定';

/** 這個人加進專案 / 建邀請連結時能選的角色：全站製作四種都可以；專案的製作沒有導演（視覺統籌可以自己加） */
export function assignableMemberRoles(viewerIsGlobalAdmin: boolean): readonly MemberRole[] {
  return viewerIsGlobalAdmin ? MEMBER_ROLE_ORDER : MEMBER_ROLE_ORDER.filter(r => r !== 'director');
}

/** 每個角色在這個專案能做什麼（選角色時的小字） */
export const MEMBER_ROLE_HINT: Record<MemberRole, string> = {
  admin: `可以編輯這個專案、自己加動畫師和 PM；${PROJECT_ADMIN_DIRECTOR_TEXT}`,
  director: `看放行的版本、留言、通過或要求修改；也能${DIRECTOR_STAGE_TEXT}`,
  visual_lead: '看內部版本、放行給導演、轉交導演意見',
  animator: '進工作站：交影片、看內部留言',
};

/**
 * 專案的「製作」能做的事（每一項都有測試去對後端的權限：cf-worker/members.test.mjs）。
 * label 是說明裡寫的字；改這裡也要改測試裡對應的請求。
 */
export const PROJECT_ADMIN_CAN: readonly string[] = ['模型', '歌單', 'cue', '機關', '版型', '分享', '專案狀態', '3D 檢視設定', '成員'];

/** 專案的「製作」還能直接打開導演頁和動畫師工作站，用自己的名字操作（上傳 / 刪影片、審片、留言、畫記）（測試：cf-worker/members.test.mjs） */
export const PROJECT_ADMIN_VIEW_TEXT = '還能直接打開導演頁和動畫師工作站（替任何一首歌交影片、刪版本、決定通過 / 需修改、留言、畫記）';

/** 專案的「製作」不能做的事（後端會擋；說明裡一起寫出來，免得以為什麼都能改） */
export const PROJECT_ADMIN_CANNOT: readonly { label: string; phrase: string }[] = [
  { label: '別的專案', phrase: '看不到別的專案' },
  { label: '全站用戶', phrase: '管全站用戶' },
  { label: 'Drive 來源', phrase: '改 Drive 來源' },
  { label: '封存或複製專案', phrase: '封存或複製專案' },
];

/** 面板上那一行說明（由上面兩個清單組出來，所以說明跟測試過的權限一定一樣） */
export const PROJECT_ADMIN_SCOPE_TEXT =
  `PM 可以編輯這個專案（${PROJECT_ADMIN_CAN.slice(0, -1).join('、')}），也可以自己加動畫師和 PM（${PROJECT_ADMIN_DIRECTOR_TEXT}），${PROJECT_ADMIN_VIEW_TEXT}；` +
  `看不到別的專案，也不能${PROJECT_ADMIN_CANNOT.slice(1).map(c => c.phrase).join('、')}`;

export interface MemberLike {
  id: string;
  name: string;
  role: MemberRole;
}

export interface MemberGroup<M extends MemberLike> {
  role: MemberRole;
  label: string;
  members: M[];
}

/** 依角色分組（製作 → 導演 → 動畫師），組內照名字排；沒有人的角色不出現 */
export function groupMembersByRole<M extends MemberLike>(members: readonly M[]): MemberGroup<M>[] {
  const out: MemberGroup<M>[] = [];
  for (const role of MEMBER_ROLE_ORDER) {
    const list = members
      .filter(m => m.role === role)
      .sort((a, b) => a.name.localeCompare(b.name, 'zh-Hant'));
    if (list.length > 0) out.push({ role, label: MEMBER_ROLE_NAME[role], members: list });
  }
  return out;
}

/** 組內人數的標題：「導演（2）」 */
export function groupTitle(label: string, count: number): string {
  return `${label}（${count}）`;
}

/**
 * 這個人的角色選單能不能改：全站製作鎖住、自己鎖住（後端也會擋）。
 * 專案的製作（viewerIsGlobalAdmin = false）看到導演那幾列也是鎖住的：導演要請全站製作（後端也會擋）
 */
export function memberRowLock(
  m: { globalAdmin?: boolean; isMe?: boolean; role?: MemberRole; pending?: boolean },
  viewerIsGlobalAdmin = true,
): null | 'globalAdmin' | 'me' | 'director' | 'pending' {
  if (m.globalAdmin) return 'globalAdmin';
  if (m.pending) return 'pending';
  if (m.isMe) return 'me';
  if (!viewerIsGlobalAdmin && m.role === 'director') return 'director';
  return null;
}

/** 還沒按連結加入的人，選單和成員清單上的字 */
export const PENDING_MEMBER_TEXT = '還沒加入';
/** 還沒加入、連結也撤銷或過期了 */
export const PENDING_LINK_DEAD_TEXT = '連結失效';

export const MEMBER_LOCK_TEXT: Record<'globalAdmin' | 'me' | 'director' | 'pending', string> = {
  pending: '等他加入之後才能改角色',
  globalAdmin: '全站製作：本來就看得到所有專案',
  me: '這是你自己：要改請別的 PM 幫你',
  director: PROJECT_ADMIN_DIRECTOR_TEXT,
};

/** 找人時要輸入幾個字才去查（後端沒有名字不會列人） */
export const CANDIDATE_MIN_CHARS = 1;
